import React, { useEffect, useState } from "react";
import UserDashboardnav from "components/nav/userdashboard";
import Footer from "components/nav/footer.component";
import ServiceSearch from "components/services/search.component";
import { useSearchParams } from "react-router-dom";
import useDebounce from "utils/hooks/useDebounce";
import { selectCurrentUser } from "store/user/user.selector";
import { useSelector } from "react-redux";
import { apiRequest } from "utils/helper/helper";
import Providers from "components/services/providers.component";
import { Business, House } from "store/service/service.types";
import { alertMessage } from "components/toolkit/initial-state.component";
import {
  fetchHouseTypes,
  houseSearchUrl,
  HouseType,
  providerSearchUrl,
} from "services/homes-api";
import {
  Coordinates,
  currentHouseRegion,
  currentPosition,
  Region,
} from "services/location";

const selectClass =
  "select select-bordered select-sm bg-white dark:bg-black rounded-full";

const Subcategory: React.FC<{ isHouse: boolean }> = ({ isHouse }) => {
  const [providers, setProviders] = useState<Business[] | House[] | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const deBounceValue: string = useDebounce(searchValue, 600);
  const [searchParams] = useSearchParams();
  const title = searchParams.get("service");
  const emptyServices = new Array(20).fill(0);
  const user = useSelector(selectCurrentUser);
  const token = user?.token;

  const [houseTypes, setHouseTypes] = useState<HouseType[]>([]);
  const [houseType, setHouseType] = useState("");
  // Providers are searched by coordinates, houses by country/state names.
  const [nearby, setNearby] = useState<Coordinates | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    if (!isHouse || !token) return;
    fetchHouseTypes(token).then((res) => {
      if (res.success && Array.isArray(res.data)) setHouseTypes(res.data);
    });
  }, [isHouse, token]);

  // Search runs on the server (the API matches more than the first page).
  useEffect(() => {
    if (!token) return;
    let active = true;
    const search = deBounceValue.trim();
    const uri = isHouse
      ? houseSearchUrl({
          search,
          type: houseType,
          country: region?.country,
          state: region?.state,
          nearby: region != null,
        })
      : providerSearchUrl({ service: title, search, lat: nearby?.lat, lng: nearby?.lng });
    setProviders(null);
    apiRequest(uri, token).then((data) => {
      if (active) setProviders(Array.isArray(data) ? data : []);
    });
    return () => {
      active = false;
    };
  }, [isHouse, title, token, deBounceValue, houseType, nearby, region]);

  const handleNearby = async (value: string) => {
    if (value !== "nearby") {
      setNearby(null);
      setRegion(null);
      return;
    }
    setLocating(true);
    try {
      if (isHouse) {
        const found = await currentHouseRegion();
        setRegion(found);
        alertMessage(
          "info",
          `Showing houses in ${[found.state, found.country].filter(Boolean).join(", ")}`
        );
      } else {
        setNearby(await currentPosition());
      }
    } catch (error) {
      alertMessage("warn", (error as Error).message);
    } finally {
      setLocating(false);
    }
  };

  const nearbySelect = (
    <select
      aria-label="Location"
      className={selectClass}
      disabled={locating}
      value={nearby || region ? "nearby" : "all"}
      onChange={(e) => handleNearby(e.target.value)}>
      <option value="all">All</option>
      <option value="nearby">{locating ? "Locating…" : "Nearby"}</option>
    </select>
  );

  return (
    <section className="w-full">
      <UserDashboardnav />
      <main className="w-full pb-24 px-6 xl:px-24">
        <div className="mt-12">
          <h1 className="capitalize text-3xl font-normal mb-7">
            Search nearby
          </h1>
          <div className="relative w-full">
            <div className="w-full px-6 lg:px-12 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="grow">
                <ServiceSearch
                  searchValue={searchValue}
                  handleChange={setSearchValue}
                />
              </div>
              {isHouse && (
                <select
                  aria-label="House type"
                  className={selectClass}
                  value={houseType}
                  onChange={(e) => setHouseType(e.target.value)}>
                  <option value="">All</option>
                  {houseTypes.map((type) => (
                    <option
                      key={type._id}
                      value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
              {nearbySelect}
            </div>
          </div>
        </div>
        <div className="flex justify-center p-8">
          <h1 className="capitalize font-semibold mt-22 text-2xl">
            {isHouse ? "Houses" : `${title} category`}
          </h1>
        </div>
        {isHouse ? (
          <Providers
            houses={providers as House[] | null}
            emptyServices={emptyServices}
          />
        ) : (
          <Providers
            providers={providers as Business[] | null}
            emptyServices={emptyServices}
          />
        )}
      </main>
      <Footer />
    </section>
  );
};

export default Subcategory;
